import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Grid,
  Typography,
  Alert,
  Container,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { useDeleteDiscountMutation } from '../services/discounts.endpoints';
import { Discount } from '../schemas/discounts.schemas';
import DiscountForm from './DiscountForm';
import DiscountsList from './DiscountsList';

const DiscountsPage: React.FC = () => {
  const [
    deleteDiscount,
    {
      isLoading: deleteIsLoading,
      error: deleteError,
      isSuccess: deleteSuccess,
    },
  ] = useDeleteDiscountMutation();

  const [isAddDialogOpen, setAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<Discount | null>(
    null,
  );

  useEffect(() => {
    if (deleteSuccess) {
      setDeleteDialogOpen(false);
    }
  }, [deleteSuccess]);

  // const [code, setCode] = useState('');
  // const [amount, setAmount] = useState<number | null>(null);
  const handleEditClick = (discount: Discount) => {
    // Set the selected discount to the one to be edited
    setSelectedDiscount(discount);
    // Open the edit dialog
    setEditDialogOpen(true);
  };

  const handleDiscountCreated = (discount: Discount) => {
    // Keep the dialog open, but flip create → edit so further saves update.
    setSelectedDiscount(discount);
    setAddDialogOpen(false);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (discount: Discount) => {
    // Set the selected discount's code to the one to be deleted
    setSelectedDiscount(discount);
    // Open the delete confirmation dialog
    setDeleteDialogOpen(true);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ p: 4 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Grid item>
            <Typography variant="h4">Discounts</Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add Discount
            </Button>
          </Grid>
        </Grid>

        <DiscountsList
          handleDeleteClick={handleDeleteClick}
          handleEditClick={handleEditClick}
        />

        <DiscountForm
          mode={isEditDialogOpen ? 'edit' : 'create'}
          discount={selectedDiscount}
          open={isEditDialogOpen || isAddDialogOpen}
          setOpen={isAddDialogOpen ? setAddDialogOpen : setEditDialogOpen}
          onCreated={handleDiscountCreated}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this discount?
            </DialogContentText>
            {deleteError && (
              <Grid item xs={12}>
                <Alert severity="error">
                  Error deleting discount: {formatQueryError(deleteError)}
                </Alert>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            {selectedDiscount && (
              <Button
                disabled={deleteIsLoading}
                onClick={() => deleteDiscount(selectedDiscount._id)}
              >
                Delete
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default DiscountsPage;
